import styled from '@emotion/styled';
import React, { useState } from 'react';
import { css } from '@emotion/core';
import { usePathname } from 'next/navigation';
import { Css, ITheme, SearchIcon, LanguageIcon } from '../../../../ui';
import { Color } from '../../../../styles/Color';
import { RouterLink } from '../../link/RouterLink';
import GoogleTranslate from '../../../../features/shared/translate/GoogleTranslate';
import { PageRoutes } from '../../../../features/app/pages/domain/constants/PageRoutes';
import { getCurrentSiteSectionSlugFromPathname } from '../utils/getCurrentSiteSectionSlugFromPathname';

interface IMainNavigationUserSectionProps {
	displaySearch?: boolean;
}

export default function MainNavigationUserSection({
	displaySearch,
}: IMainNavigationUserSectionProps) {
	const pathname = usePathname();
	const siteSectionSlug = getCurrentSiteSectionSlugFromPathname(pathname);
	const [showTranslateBar, setShowTranslateBar] = useState(false);

	const toggleTranslateBar = () => {
		setShowTranslateBar((prev) => !prev);
	};

	return (
		<UserSectionWrap className="nav-user-section">
			<GoogleTranslate showDropdown={showTranslateBar} />
			<LanguageButton
				onClick={toggleTranslateBar}
				aria-label="Select a new language to translate the website"
			>
				<LanguageIcon aria-hidden />
				<StyledStaticLinkLabel>Language</StyledStaticLinkLabel>
			</LanguageButton>

			{displaySearch && (
				<StyledStaticLink
					href={`/${siteSectionSlug}/${PageRoutes.Search}`}
					aria-label="Navigate to search page"
				>
					<SearchIcon aria-hidden />
					<StyledStaticLinkLabel>Search</StyledStaticLinkLabel>
				</StyledStaticLink>
			)}
		</UserSectionWrap>
	);
}

const UserSectionWrap = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: ${Css.pxToRem(24)};
	position: relative;
`;

const StyledStaticLinkLabel = styled.div`
	align-self: center;
	font-weight: 700;
	font-size: ${Css.pxToRem(16)};
	color: ${Color.OnBackground};
	text-transform: capitalize;
`;

export const staticLinkStyles = css`
	text-decoration: none;
	border: none;
	cursor: pointer;
	background: none;
	padding: 0;
	display: flex;
	flex-direction: row;
	gap: ${Css.pxToRem(8)};
	width: fit-content;
	height: ${Css.pxToRem(24)};
	z-index: 101;
`;

const StyledStaticLink = styled(RouterLink)<{}, ITheme>`
	${staticLinkStyles}

	svg {
		path {
			fill: ${(props) => props.theme.color.secondaryLight};
		}
	}

	&:hover {
		${StyledStaticLinkLabel} {
			color: ${(props) => props.theme.color.brand};
		}
		svg {
			path {
				fill: ${(props) => props.theme.color.brand};
			}
		}
	}
`;

export const LanguageButton = styled.button<{}, ITheme>`
	${staticLinkStyles}
	font-family: inherit;

	svg {
		path {
			fill: ${(props) => props.theme.color.secondaryLight};
		}
	}

	&:hover {
		color: ${(props) => props.theme.color.brand};

		${StyledStaticLinkLabel} {
			color: ${(props) => props.theme.color.brand};
		}
		svg {
			path {
				fill: currentColor;
			}
		}
	}
`;
